import type { CameraView } from '@/math'
import type { EventTuple } from '@/events/types'
import type { EphemeralState, ScribbleSettings } from '@/uiState'
import type { VoxelMap, Hit, VoxelData } from '@/voxelTypes'
import { posKey, posKeyXYZ, parseKey, offsetVoxels } from '@/voxelMath'
import { e as makeEvent } from '@/events/types'
import { computeLineEnd } from '@/interactions/scribbleHelpers'
import { spineEntry, type Spine } from '@/interactions/spineCore'
import { Position } from '@/voxelMath'
import { hoverSuppression } from '@/state/hoverSuppression'
import { makeShapeBuilder } from '@/builders/shapeBuilder'
import { centerFromStartForShape, normalizeShapeDimensions } from '@/core/shapes'
import { isInBoundsXYZ } from '@/voxelMath'
import { toFx } from '@/brush/fixed'
import { hoverSeed, lumpSnapshotFields, resolveBrushLump } from '@/brush/lump'
import { BRUSH_STROKE, type BrushSnapshot, type BrushStrokePayload } from '@/events/brushStroke'
import { brushPolicy, effectiveBrushRadius, eraserFloorY, eraserForcesSwept, eraserKeepTypes, resolveDigScope, strokeVerbWithOptions } from '@/voxels/brushPolicy'
import { canonicalVoxelType, type DigScope } from '@/voxels/registry'
import { hoverBudgetKey, resolveHoverPreviewPath, toolHoverPreviewMode, type HoverPreviewMode } from '@/voxels/hoverPreviewMode'

// ----------------------------------------------------------------------------
// Hover preview (large-volume plan S12 / Q6)
// ----------------------------------------------------------------------------

/**
 * How an idle pen/eraser hover is previewed:
 * - `ghost`: no preview event; `src/fiber/BrushGhost.tsx` draws a translucent sphere instead.
 * - `brush`: one-sample `brush.stroke` (a single crescent-free sphere stamp on the composed grid).
 * - `legacy`: a single-point `scribble.apply`, where the brush does not sweep (cube/disc shapes,
 *   `onTop` placement, or a type whose brush policy does not sweep at this radius).
 */
export type HoverBrushPath = 'legacy' | 'brush' | 'ghost'

export function resolveHoverBrushPath(
  ui: EphemeralState,
  voxelData: VoxelData | undefined,
  subtract: boolean,
  hoverOverride: HoverPreviewMode | null,
): HoverBrushPath {
  const settings = ui.tool.scribble ?? {}
  // B2: the hover uses the same clamped radius as the committed stroke (`maxRadius` of the type
  // being placed; the eraser carries no type and keeps the UI radius).
  const radius = hoverBrushRadius(ui, voxelData, subtract)
  const shape = settings.shape ?? 'sphere'
  const placement = settings.placement ?? 'center'
  const mode = toolHoverPreviewMode(ui.tool, subtract, hoverOverride)
  const key = hoverBudgetKey(subtract, voxelData?.type)
  if (resolveHoverPreviewPath(mode, radius, shape, placement, key) === 'ghost') return 'ghost'
  // Same gate as the controller's beginBrushSampler: plain centred sphere brush, and the type's brush
  // policy (or a swept-only eraser option) allows sweeping at this radius.
  const eraserSwept = subtract && eraserForcesSwept(ui.tool.eraser)
  if (shape !== 'sphere' || placement !== 'center') return 'legacy'
  if (!subtract && !voxelData) return 'legacy'
  const type = voxelData?.type ?? 'solid'
  return eraserSwept || brushPolicy(type, radius, subtract).swept ? 'brush' : 'legacy'
}

/** The hover's effective radius: the UI radius clamped by the placed type's `maxRadius` (B2). */
export function hoverBrushRadius(ui: EphemeralState, voxelData: VoxelData | undefined, subtract: boolean): number {
  return effectiveBrushRadius(subtract ? undefined : voxelData?.type, ui.tool.scribble?.radius)
}

/**
 * Eraser scope of a hover or stroke that starts on `startVoxel` (B5, Q11): the eraser option wins,
 * else the registry `digScope` of the type under the first hit. Null when the erase is unscoped.
 */
function hoverEraseScope(ui: EphemeralState, startVoxel: VoxelData | undefined): { digScope: DigScope; onlyValue: VoxelData } | null {
  if (!startVoxel) return null
  const scope = resolveDigScope(ui.tool.eraser?.scope, startVoxel.type)
  return scope === 'self' ? { digScope: scope, onlyValue: startVoxel } : null
}

/**
 * The cell a hover brush is centred on: today's placement rule (spineCore.placeSpineCenters and the
 * controller's brushDeltaEvent). For voxel hits in draw mode, the free cell adjacent along the
 * normal; otherwise the hit cell.
 */
export function resolveHoverBrushCenter(hit: Hit, base: VoxelMap, bounds: Position, subtract: boolean): Position {
  const { x, y, z } = hit.position
  if (!subtract && hit.type === 'voxel') {
    const n = hit.normal || { x: 0, y: 0, z: 0 }
    const ax = x + (Math.round(n.x) | 0)
    const ay = y + (Math.round(n.y) | 0)
    const az = z + (Math.round(n.z) | 0)
    if (isInBoundsXYZ(ax, ay, az, bounds) && !base.has(posKeyXYZ(ax, ay, az))) return { x: ax, y: ay, z: az }
  }
  return { x, y, z }
}

function makeOneSampleBrushStrokeEvent(
  ui: EphemeralState,
  voxelData: VoxelData | undefined,
  center: Position,
  subtract: boolean,
  startVoxel: VoxelData | undefined,
): EventTuple {
  // Same snapshot the controller's beginBrushSampler would commit: clamped radius, the type's
  // stroke verb ('fill' for poured materials), and a scoped `subtractIf` for an eraser that starts
  // on a type whose dig scope resolves to 'self'.
  const radius = hoverBrushRadius(ui, voxelData, subtract)
  const type = voxelData?.type ?? 'solid'
  const scope = subtract ? hoverEraseScope(ui, startVoxel) : null
  const policy = brushPolicy(type, radius, subtract, scope ? { digScope: scope.digScope, startHitType: scope.onlyValue.type } : undefined)
  const brush: BrushSnapshot = {
    v: 1,
    verb: subtract ? policy.verb : strokeVerbWithOptions(policy.verb, ui.tool.scribble),
    rFx: toFx(radius),
    ...(!subtract && voxelData ? { value: voxelData } : {}),
    ...(policy.scopeType && scope ? { onlyValue: scope.onlyValue } : {}),
    // Protected types: the preview never shows them erased.
    ...(subtract && eraserKeepTypes(ui.tool.eraser) ? { keep: eraserKeepTypes(ui.tool.eraser) } : {}),
    // A dig's floor: the preview never shows the bedrock going.
    ...(subtract && eraserFloorY(ui.tool.eraser) !== undefined ? { floorY: eraserFloorY(ui.tool.eraser) } : {}),
    // Lumpy shell (B4) with a seed that depends only on the hovered cell, so the preview event is
    // stable while the cursor stays inside one cell (A1).
    ...(subtract ? {} : lumpSnapshotFields(resolveBrushLump(type, radius), hoverSeed(posKeyXYZ(center.x, center.y, center.z)))),
  }
  const payload: BrushStrokePayload = {
    brush,
    samples: [toFx(center.x), toFx(center.y), toFx(center.z)],
  }
  return makeEvent([BRUSH_STROKE, payload] as const)
}

/**
 * Legacy scribble settings for a preview: the UI settings with the radius clamped as the committed
 * stroke's would be (B2), plus `onlyType` when an erase starting on `startVoxel` is scoped (B5).
 */
function previewScribblePayloadFields(
  ui: EphemeralState,
  voxelData: VoxelData | undefined,
  subtract: boolean,
  startVoxel: VoxelData | undefined,
): { scribble: ScribbleSettings; onlyType?: string } {
  const settings = ui.tool.scribble ?? {}
  const scribble: ScribbleSettings = { ...settings, radius: hoverBrushRadius(ui, voxelData, subtract) }
  const scope = subtract ? hoverEraseScope(ui, startVoxel) : null
  return scope ? { scribble, onlyType: canonicalVoxelType(scope.onlyValue.type) } : { scribble }
}

function makeSinglePointScribbleEvent(
  ui: EphemeralState,
  voxelData: VoxelData,
  hit: Hit,
  subtract: boolean,
  base: VoxelMap,
): EventTuple | null {
  if (!hit?.position) return null
  const center = hit.position
  const k = posKeyXYZ(center.x, center.y, center.z)
  const spine: Spine = [{ key: k, ...spineEntry(voxelData, hit) }]
  const startVoxel = subtract ? base.get(k) : undefined
  return makeEvent(['scribble.apply', {
    spine,
    subtract,
    ...previewScribblePayloadFields(ui, voxelData, subtract, startVoxel),
  }] as const)
}

export function buildPreviewEvents(args: {
  ui: EphemeralState
  voxelData: VoxelData
  base: VoxelMap
  bounds: Position
  camera: CameraView | null
  /** A page's hover mode override (S12); null or unset = the tools' own modes. */
  hoverOverride?: HoverPreviewMode | null
  /** Called when an idle hover is previewed as real voxels (the measured hover budget). */
  onRealHover?: (key: string, radius: number) => void
}): EventTuple[] {
  const { ui, voxelData, base, bounds, camera } = args
  const hoverOverride = args.hoverOverride ?? null
  const events: EventTuple[] = []

  const hoverSuppressed = hoverSuppression.isSuppressed()
  const currentTool = ui.tool.currentTool
  const hit = ui.cursorHit as Hit | null
  const scribbleSpine = (ui.scribbleSpine as Spine | null) || null
  const eraserFillMode = currentTool === 'eraser' && ui.tool.eraser?.mode === 'fill'
  // Brush preview
  if (!hoverSuppressed && currentTool === 'paintbrush') {
    const spine: Map<number, { normal?: { x: number; y: number; z: number } }> =
      (ui.paintSpine && ui.paintSpine.size > 0)
        ? ui.paintSpine
        : (hit?.position
            ? new Map([[posKeyXYZ(hit.position.x, hit.position.y, hit.position.z), { normal: hit ? { x: hit.normal.x, y: hit.normal.y, z: hit.normal.z } : undefined }]])
            : new Map())
    if (spine.size > 0) {
      events.push(makeEvent(['paint.stroke', {
        spine,
        paint: ui.tool.paint,
        scribble: ui.tool.scribble,
        voxel: voxelData,
        mode: 'color',
        seedNormal: hit ? { x: hit.normal.x, y: hit.normal.y, z: hit.normal.z } : undefined,
      }] as const))
    }
  }

  // Pen/Eraser preview
  if (!hoverSuppressed && (currentTool === 'scribble' || (currentTool === 'eraser' && !eraserFillMode)) && !ui.rectangleBounds) {
    if (scribbleSpine && scribbleSpine.length > 0) {
      const subtract = currentTool === 'eraser'
      // The stroke's first spine cell is where it started: its voxel decides a scoped erase.
      const startVoxel = subtract ? base.get(scribbleSpine[0].key) : undefined
      events.push(makeEvent(['scribble.apply', {
        spine: scribbleSpine,
        subtract,
        ...previewScribblePayloadFields(ui, voxelData, subtract, startVoxel),
      }] as const))
    } else if (hit?.position) {
      // Idle hover (S12): ghost = no event (BrushGhost draws the volume); brush = one-sample
      // brush.stroke; legacy = single-point scribble.apply.
      const subtract = currentTool === 'eraser'
      const path = resolveHoverBrushPath(ui, voxelData, subtract, hoverOverride)
      if (path === 'brush') {
        const center = resolveHoverBrushCenter(hit, base, bounds, subtract)
        const startVoxel = subtract ? base.get(posKey(hit.position)) : undefined
        events.push(makeOneSampleBrushStrokeEvent(ui, voxelData, center, subtract, startVoxel))
      } else if (path === 'legacy') {
        const ev = makeSinglePointScribbleEvent(ui, voxelData, hit, subtract, base)
        if (ev) events.push(ev)
      }
      if (path !== 'ghost') args.onRealHover?.(hoverBudgetKey(subtract, voxelData?.type), hoverBrushRadius(ui, voxelData, subtract))
    }
  }

  // Line preview
  if (!hoverSuppressed && currentTool === 'line' && hit) {
    const anchor = scribbleSpine?.[0]
    let previewSpine: Spine
    if (anchor) {
      const end = computeLineEnd(anchor.key, ui as any, base, bounds, camera)
      if (end) {
        previewSpine = [
          { ...anchor },
          { key: end, voxel: anchor.voxel, normal: anchor.normal, hitType: anchor.hitType },
        ]
      } else {
        previewSpine = [{ ...anchor }]
      }
    } else {
      const anchorPoint = { key: posKey(hit.position), ...spineEntry(voxelData, hit) }
      previewSpine = [anchorPoint]
    }
    events.push(makeEvent(['scribble.apply', { spine: previewSpine, scribble: ui.tool.scribble!, subtract: false }] as const))
  }

  // Spline preview
  if (!hoverSuppressed && currentTool === 'spline') {
    if (scribbleSpine && scribbleSpine.length > 0) {
      const last = scribbleSpine[scribbleSpine.length - 1]
      const endKey = computeLineEnd(last.key, ui as any, base, bounds, camera)
      if (endKey != null) {
        const first = scribbleSpine[0]
        const a = parseKey(first.key)
        const b = parseKey(endKey)
        const scribbleRadius = Math.max(0, (ui?.tool?.scribble?.radius ?? 0))
        const closeThresh = Math.max(3, Math.ceil(scribbleRadius))
        const manhattan = Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.z - b.z)
        const isClosed = scribbleSpine.length >= 3 && manhattan <= closeThresh
        if (isClosed) {
          const mainScribble = { ...(ui.tool.scribble || {}), shouldConnect: true }
          events.push(makeEvent(['scribble.apply', { spine: scribbleSpine.slice(), scribble: mainScribble, subtract: false }] as const))
          const closeSpine: Spine = [{ ...last }, { ...first }]
          events.push(makeEvent(['scribble.apply', { spine: closeSpine, scribble: mainScribble, subtract: false }] as const))
        } else {
          const previewSpine: Spine = [
            ...scribbleSpine.map((point) => ({ ...point })),
            { key: endKey, ...spineEntry(voxelData, hit as any) },
          ]
          const scribble = { ...(ui.tool.scribble || {}), shouldConnect: true }
          events.push(makeEvent(['scribble.apply', { spine: previewSpine, scribble, subtract: false }] as const))
        }
      }
    } else if (hit?.position) {
      const ev = makeSinglePointScribbleEvent(ui, voxelData, hit, false, base)
      if (ev) events.push(ev)
    }
  }

  // Rectangle paint preview
  if (!hoverSuppressed && ui.isDrawing && ui.rectangleBounds) {
    const min = ui.rectangleBounds.min
    const max = ui.rectangleBounds.max
    const normal = ui.rectangleInitialNormal || undefined
    const isEraser = ui.tool.currentTool === 'eraser'
    const mode: 'place' | 'fill' | 'subtract' = isEraser ? 'subtract' : (ui.tool.rectangle.fillMode ? 'fill' : 'place')
    events.push(['paint.rectangle', { min, max, normal: normal || undefined, mode, voxel: voxelData }])
  }

  // Shape preview
  if (!hoverSuppressed && (currentTool === 'shape') && ui.shapeState) {
    const shape = ui.shapeState
    if (shape.center) {
      const type = (shape.type || 'sphere') as 'sphere' | 'cylinder' | 'cone'
      const axis = (shape.axis || 'y') as 'x' | 'y' | 'z'
      const axisSign = (shape.axisSign ?? 1) as 1 | -1
      const { radius, height } = normalizeShapeDimensions(type, shape.radius, shape.height)
      const center = shape.startPosition
        ? centerFromStartForShape(type, shape.startPosition, radius, height, axis, axisSign)
        : shape.center
      events.push(['geom.shape', { type, center, radius, height, axis, axisSign, voxel: voxelData }])
    }
  }

  // Selection translation preview
  const sel = ui.voxelSelection
  if (sel) {
    const t = sel.translate
    const hasTransform = !!t && (t.x !== 0 || t.y !== 0 || t.z !== 0)
    if (hasTransform) {
      events.push(['voxel.blend', sel.keys, 'subtract'])
      const selectionVoxels = offsetVoxels(sel.keys || [], t, bounds, base)
      if (selectionVoxels.size > 0) events.push(['voxel.blend', selectionVoxels as any, 'place'])
    }
  }

  // Face extrusion preview
  const fs = ui.faceSelection
  const faceOffset = fs?.offset || 0
  if (fs && faceOffset) {
    if (fs.keys) {
      events.push(['voxel.extrudeFaces', { face: fs.face as any, keys: Array.from(fs.keys as Set<number>), offset: faceOffset, fillMode: !!ui.tool.rectangle.fillMode }])
    } else if (fs.anchor) {
      events.push(['voxel.extrude', { anchor: fs.anchor, face: fs.face as any, offset: faceOffset, fillMode: !!ui.tool.rectangle.fillMode }])
    }
  }

  // Subject/Builder preview (single active)
  // For now, derive builder preview from UI (registry not available here)
  const subjectRef = (ui as any).activeSubjectRef as { kind: string; id: string } | null
  if (subjectRef?.kind === 'builder' && (ui as any).builderSubject) {
    const bs = (ui as any).builderSubject as { id: string; params: any }
    if (bs && bs.id === subjectRef.id) {
      const key = `${subjectRef.kind}:${subjectRef.id}`
      const t = (ui as any).subjectTransforms?.[key]
      const translate = t?.translate || { x: 0, y: 0, z: 0 }
      const rotation = t?.rotation || { x: 0, y: 0, z: 0, w: 1 }
      const builder = makeShapeBuilder({ ...(bs.params || {}), rotation }, translate)
      const subjectEvents = builder.previewEvents?.()
      if (Array.isArray(subjectEvents) && subjectEvents.length > 0) {
        for (const ev of subjectEvents) events.push(ev)
      }
    }
  }

  return events
}
